import { ATTACK_PATH_COLUMNS, type AttackPathLink, type AttackPathNode, type AttackPathsModel } from './attack-paths.ts'

/**
 * Places the attack-path columns, and shapes the curves between them. Pure — no DOM, no React — so the
 * arithmetic can be tested without rendering anything.
 *
 * **Rows keep payload order.** Not sorted, and not reordered to reduce crossings: the generator's order
 * is the order the Lists view shows one tab away, and the same actor being third in both places is worth
 * more than a tidier picture. Crossing-minimisation would also be optimising the layout against the
 * placeholder edges, so it would visibly rearrange itself the day the real references arrive.
 *
 * **Height grows with the tallest column** rather than being fixed. The design mock divides a fixed
 * canvas by the row count, which holds at nine rows and collapses past that — twenty entry points would
 * leave 21px of box for two lines of text. Since the viewport pans and zooms, a taller canvas costs
 * nothing and keeps every row legible.
 */

/**
 * Wider than the five columns strictly need. At 1200 the gap between one card's right edge and the next
 * card's left was ~70px, so nineteen curves crossed each other inside it and the picture read as hatching
 * rather than as paths. The extra width all goes into those gaps — the cards themselves do not grow — and
 * the viewport scales the whole thing to fit, so a wider canvas costs nothing on screen.
 */
const WIDTH = 1560
const NODE_WIDTH = 174
const NODE_HEIGHT = 46
const ROW_GAP = 26
const ROW_PITCH = NODE_HEIGHT + ROW_GAP
/** Room for the column headings above the first row. */
const HEADER_HEIGHT = 92
const BOTTOM_PAD = 24
const SIDE_PAD = 26

export const ATTACK_PATH_NODE_RADIUS = 7
export const ATTACK_PATH_NODE_SIZE = { width: NODE_WIDTH, height: NODE_HEIGHT }

export type PlacedAttackPathNode = AttackPathNode & {
  x: number
  y: number
  /** Right edge and vertical centre — where a curve leaves this node. */
  exitX: number
  centerY: number
}

export type PlacedAttackPathLink = AttackPathLink & {
  /** SVG path data for the whole curve. */
  d: string
  /**
   * The same curve cut in two at its midpoint, plus that point.
   *
   * Both control points sit on the midline, so `t = 0.5` lands exactly on
   * `((exitX + toX) / 2, (fromY + toY) / 2)` — and because the columns are evenly spaced, an
   * entry-point-to-weakness curve crosses the security-control column right there. That is where a
   * control stands in the route, so it is where the run turns conditional: solid up to the gate,
   * dashed after it.
   */
  gate: { at: { x: number; y: number }; before: string; after: string }
}

export type PlacedAttackPathColumn = {
  key: (typeof ATTACK_PATH_COLUMNS)[number]['key']
  title: string
  x: number
  count: number
}

export type AttackPathLayout = {
  contentSize: { width: number; height: number }
  columns: PlacedAttackPathColumn[]
  nodes: PlacedAttackPathNode[]
  links: PlacedAttackPathLink[]
}

/** A column's rows occupy this much vertical space, with no trailing gap after the last one. */
const columnHeight = (count: number): number => (count === 0 ? 0 : count * ROW_PITCH - ROW_GAP)

/**
 * A cubic with both control points on the midline, which is the standard shape for this diagram: it
 * leaves and arrives horizontally, so a curve reads as continuing the row it came from.
 */
const curveBetween = (from: PlacedAttackPathNode, to: PlacedAttackPathNode): string => {
  const midX = (from.exitX + to.x) / 2
  return `M${from.exitX},${from.centerY} C${midX},${from.centerY} ${midX},${to.centerY} ${to.x},${to.centerY}`
}

type Point = { x: number; y: number }

const mid = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 })

const curveData = (p0: Point, p1: Point, p2: Point, p3: Point): string =>
  `M${p0.x},${p0.y} C${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`

/**
 * Splits the curve at its halfway point, exactly — De Casteljau rather than sampling, so the two halves
 * meet with no seam and the gate marker sits precisely on the join.
 */
const splitCurve = (from: PlacedAttackPathNode, to: PlacedAttackPathNode) => {
  const midX = (from.exitX + to.x) / 2
  const p0 = { x: from.exitX, y: from.centerY }
  const p1 = { x: midX, y: from.centerY }
  const p2 = { x: midX, y: to.centerY }
  const p3 = { x: to.x, y: to.centerY }

  const a = mid(p0, p1)
  const b = mid(p1, p2)
  const c = mid(p2, p3)
  const d = mid(a, b)
  const e = mid(b, c)
  const at = mid(d, e)

  return { at, before: curveData(p0, a, d, at), after: curveData(at, e, c, p3) }
}

export const layOutAttackPaths = ({ nodes, links }: AttackPathsModel): AttackPathLayout => {
  // Only columns that actually hold nodes are placed, so a pre-linking document draws two
  // evenly-spaced columns — controls and weaknesses — rather than five with three empty ones. Order
  // follows ATTACK_PATH_COLUMNS, so whatever subset is drawn still reads left to right, and the even
  // spacing keeps an entry→weakness curve crossing the control column at its midpoint.
  const activeColumns = ATTACK_PATH_COLUMNS.filter(column => nodes.some(node => node.column === column.key))

  // One column has no gap to divide; place it flush left. In practice the graph always carries at
  // least controls and weaknesses, so this is a guard rather than a common path.
  const columnStep = activeColumns.length > 1 ? (WIDTH - 2 * SIDE_PAD - NODE_WIDTH) / (activeColumns.length - 1) : 0

  const byColumn = activeColumns.map((column, columnIndex) => ({
    ...column,
    x: SIDE_PAD + columnIndex * columnStep,
    members: nodes.filter(node => node.column === column.key),
  }))

  const tallest = Math.max(...byColumn.map(column => column.members.length), 0)
  const bandHeight = columnHeight(tallest)
  const height = HEADER_HEIGHT + bandHeight + BOTTOM_PAD

  // Each column is centred in the band, so a short column sits opposite the middle of a long one
  // rather than hanging from the top and leaving the curves to climb.
  const placed: PlacedAttackPathNode[] = byColumn.flatMap(column => {
    const top = HEADER_HEIGHT + (bandHeight - columnHeight(column.members.length)) / 2
    return column.members.map((node, rowIndex) => {
      const y = top + rowIndex * ROW_PITCH
      return {
        ...node,
        x: column.x,
        y,
        exitX: column.x + NODE_WIDTH,
        centerY: y + NODE_HEIGHT / 2,
      }
    })
  })

  const positions = new Map(placed.map(node => [node.id, node]))

  return {
    contentSize: { width: WIDTH, height },
    columns: byColumn.map(({ key, title, x, members }) => ({ key, title, x, count: members.length })),
    nodes: placed,
    // A link naming a node this document does not contain is dropped rather than drawn to nowhere.
    links: links.flatMap(link => {
      const from = positions.get(link.from)
      const to = positions.get(link.to)
      if (!from || !to) {
        return []
      }
      return [{ ...link, d: curveBetween(from, to), gate: splitCurve(from, to) }]
    }),
  }
}

/**
 * Every node and edge reachable from one node, walking forwards and backwards separately so the whole
 * chain through it lights up rather than only what follows it. Direction is read from column position
 * rather than from edge order, so it stays correct for the curves that skip the control column.
 */
export const traceAttackPath = (
  startId: string,
  layout: AttackPathLayout
): { nodeIds: Set<string>; linkKeys: Set<string> } => {
  const nodeIds = new Set([startId])
  const linkKeys = new Set<string>()
  const positions = new Map(layout.nodes.map(node => [node.id, node]))

  const walk = (direction: 'forward' | 'back') => {
    let frontier = [startId]
    const seen = new Set([startId])
    while (frontier.length > 0) {
      const next: string[] = []
      for (const id of frontier) {
        for (const link of layout.links) {
          const isFrom = link.from === id
          const otherId = isFrom ? link.to : link.from
          if (!isFrom && link.to !== id) {
            continue
          }
          const here = positions.get(id)
          const there = positions.get(otherId)
          if (!here || !there) {
            continue
          }
          const isForward = there.x > here.x
          if (direction === 'forward' ? !isForward : isForward) {
            continue
          }
          linkKeys.add(attackPathLinkKey(link))
          nodeIds.add(otherId)
          if (!seen.has(otherId)) {
            seen.add(otherId)
            next.push(otherId)
          }
        }
      }
      frontier = next
    }
  }

  walk('forward')
  walk('back')
  return { nodeIds, linkKeys }
}

export const attackPathLinkKey = (link: Pick<AttackPathLink, 'from' | 'to'>): string => `${link.from}->${link.to}`
